import * as fs from "node:fs";
import * as path from "node:path";
import { parse as parseYaml } from "yaml";

export interface ResolvedPersona {
  persona: string;
  personaVersion: string | null;
}

/** Walk up from `startDir` looking for a directory that contains `personas/`. */
function findRepoRoot(startDir: string): string | null {
  let dir = path.resolve(startDir);
  for (;;) {
    if (fs.existsSync(path.join(dir, "personas"))) return dir;
    const parent = path.dirname(dir);
    if (parent === dir) return null;
    dir = parent;
  }
}

/**
 * Resolve a persona name to its metadata. `personaVersion` is best-effort:
 * read from personas/<persona>/persona.md frontmatter `version`, else null.
 * Never throws — a missing persona file just yields a null version.
 */
export function resolvePersona(persona: string, cwd: string = process.cwd()): ResolvedPersona {
  const root = findRepoRoot(cwd);
  if (!root) return { persona, personaVersion: null };

  const file = path.join(root, "personas", persona, "persona.md");
  let raw: string;
  try {
    raw = fs.readFileSync(file, "utf-8");
  } catch {
    return { persona, personaVersion: null };
  }

  // \r?\n tolerates CRLF line endings (Windows git checkouts) as well as LF.
  const match = /^---\r?\n([\s\S]*?)\r?\n---/.exec(raw);
  if (!match) return { persona, personaVersion: null };

  let fm: { version?: unknown } | null = null;
  try {
    fm = parseYaml(match[1]) as { version?: unknown } | null;
  } catch {
    return { persona, personaVersion: null };
  }

  const version = fm?.version;
  return {
    persona,
    personaVersion:
      typeof version === "string" || typeof version === "number"
        ? String(version)
        : null,
  };
}
